import {Directive, EventEmitter, HostListener, Input, Output} from '@angular/core';

import {ExcFileItem} from '../models/ExcFileItem';
import {ExcUploadDialogComponent} from '../exc-upload-dialog/exc-upload-dialog.component';
import {DynamicInputsInterface} from '../interfaces/DynamicInputs.interface';
import {ExcTranslation} from 'exc-core';
import {OverlayConfig, OverlayRef, OverlayService} from 'exc-controls';

/**
 * A directive that start an upload dialog which enables the adding or editing of uploads.
 * An upload is a arbitrary amount of input fields and a arbitrary amount of file inputs. After the form is submitted
 * from each file that was given, a ExcFileItem is created where the arbitrary input field values are the misc values
 * of the ExcFileItem.
 */
@Directive({
  'selector': '[excUpload]'
})
export class ExcUploadDirective extends ExcTranslation {
  /**
   * A number between 0 and 100 that describes the progress of the upload in percent
   */
  private _progress: number;
  @Input() set progress(value: number) {
    if (this.uploadFormComponent) {
      this.uploadFormComponent.progress = value;
      this._progress = value;
    }
  }

  get progress(): number {
    return this._progress;
  }

  /**
   * Emits an event after an upload was added
   */
  @Output('addUpload') addUploadEmitter: EventEmitter<ExcFileItem[]> = new EventEmitter();

  /**
   * Emits an event after an upload was edited
   */
  @Output('editUpload') editUploadEmitter: EventEmitter<ExcFileItem[]> = new EventEmitter();

  private overlayRef: OverlayRef;

  /**
   * The upload form component
   */
  private uploadFormComponent: ExcUploadDialogComponent;

  /**
   * A list of objects that present the dynamic input fields of the form upload component.
   */
  @Input() dynamicInputs: DynamicInputsInterface[] = [
    {
      apiValue: 'Label',
      displayValue: 'Name'
    }
  ];
  @Input() additionalAudits: any[] = [];
  @HostListener('click') onClick() {
    this.startAddDialog();
  }

  constructor(
    private overlayService: OverlayService
  ) {
    super();
  }


  /**
   * Starts the upload dialog. Either a edit dialog or a add dialog will be started, regarding
   * of the param that is passed.
   * @param upload
   * @param additionalAudits
   */
  startUploadDialog(upload?: ExcFileItem, additionalAudits: any[] = []) {
    this.additionalAudits = additionalAudits;
    if (upload) {
      this.startEditDialog(upload);
    } else {
      this.startAddDialog();
    }
  }

  /**
   * Opens a modal to add a new upload
   */
  private startAddDialog() {
    const config: OverlayConfig = {
      settings: {
        closableViaOverlay: false,
        header: true,
        resources: {
          headerText: this.getGlobal('Upload.AddUploadHeaderText')
        },
        footer: true,
        templateButtons: true
      },
      data: {
        dynamicInputs: this.dynamicInputs,
        additionalAudits: this.additionalAudits
      }
    };
    this.overlayRef = this.overlayService.showModal(ExcUploadDialogComponent, config);
    // observe for submitting of upload form after component is ready!
    this.overlayRef.componentReady
      .subscribe(component => {
        this.uploadFormComponent = component;
        this.uploadFormComponent.afterSubmit.subscribe(formdata => this.addUploadEmitter.emit(formdata));
      });
  }

  /**
   * Opens a modal to edit an upload
   * @param upload - upload instance to edit
   */
  private startEditDialog(upload: ExcFileItem) {
    const config: OverlayConfig = {
      settings: {
        closableViaOverlay: false,
        header: true,
        resources: {
          headerText: this.getGlobal('Upload.EditUploadHeaderText')
        },
        footer: true,
        templateButtons: true
      },
      data: {
        upload: upload,
        dynamicInputs: this.dynamicInputs,
        additionalAudits: this.additionalAudits
      }
    };
    this.overlayRef = this.overlayService.showModal(ExcUploadDialogComponent, config);

    // observe for form submitting after modal component is ready
    this.overlayRef.componentReady.subscribe(component => {
      this.uploadFormComponent = component;
      this.uploadFormComponent.afterSubmit.subscribe(editedUpload => this.editUploadEmitter.emit(editedUpload));
    });
  }

  /**
   * Closes the upload form modal
   */
  close() {
    if (this.overlayRef) {
      this.overlayRef.close();
      this.overlayRef = null;
    }
  }

  /**
   * Starts a loading animation
   */
  startLoadingAnimation() {
    this.uploadFormComponent.startLoadingAnimation();
  }

  /**
   * Ends a loading animation
   */
  stopLoadingAnimation() {
    this.uploadFormComponent.stopLoadingAnimation();
  }
}
